import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
	Box,
	H3,
	H4,
	Input,
	Link,
	Panel,
	Select,
	Tooltip,
} from '@bigcommerce/big-design';
import { LineItemEditForm } from './LineItemEditForm';
import './LineItems.css';
import { OrderComment } from './OrderComments';
import {
	StyledTable,
	StyledTableBody,
	StyledTableDataCell,
	StyledTableHead,
	StyledTableHeaderCell,
	StyledTableRow,
} from '../../../../components/styled/Table';
import { useClient } from '../../../../context/client';
import { createSelectOptions } from '../../../../utils';
import {
	getOptionsKeyValue,
	getProductVehicle,
	getStandaloneAddonVehicle,
	shouldNestAddonLineItem,
} from '../../../../utils/factoryStandaloneAddons';
import { ORDER_ACTIONS } from '../hooks/useOrderEditState';

const displayNamesToIgnore = [
	'color',
	'price code',
	'vehicle',
	'row',
	'description',
	'diagram',
	'options',
];

const highQuantityStyle = {
	color: 'red',
	fontWeight: 'bold',
	fontStyle: 'italic',
	fontSize: '2rem',
};

/**
 * Takes the product and builds out the correct format to be added to the product.questions object
 * this is only for displaying every added variation under the descriptions' column under questions.
 * @param bcProduct is the bcOrderProduct
 * @returns an object that will follow the question: answer format.
 */

function createVariationQuestions(bcProduct) {
	return (bcProduct?.product_options ?? []).reduce(
		(variantQuestionObject, variantQuestionOption) => {
			if (
				!displayNamesToIgnore.includes(
					variantQuestionOption.display_name.toLowerCase(),
				)
			) {
				return {
					...variantQuestionObject,
					[variantQuestionOption.display_name]:
						variantQuestionOption.display_value,
				};
			}
			return variantQuestionObject;
		},
		{},
	);
}

/**
 * @param {*} order the BC order object
 * @returns Float. The subtotal after converting to exchange rate.
 */
function getOriginalSubtotal(order) {
	const orSubT = order.original_subtotal;
	const excRate = order.store_default_to_transactional_exchange_rate;

	return parseFloat(orSubT * excRate).toFixed(2);
}

/**
 * @param {*} order the BC order object
 * @returns Float. The calculated product discount.
 */
function getProductDiscount(order) {
	const subtExTax = parseFloat(order.subtotal_ex_tax).toFixed(2);
	const totalExTax = parseFloat(order.total_ex_tax).toFixed(2);
	const excRate = order.store_default_to_transactional_exchange_rate;
	const orSubT = parseFloat(order.original_subtotal).toFixed(2);
	const orSubTotal = orSubT * excRate;
	const promoDisc = subtExTax - totalExTax;
	const prodDisc = orSubTotal - totalExTax - promoDisc;

	return parseFloat(prodDisc).toFixed(2);
}

/**
 * @param {*} order
 * @returns String The calculated percentage amount.
 */
function getDiscountPerc(order) {
	const totalDisc = getProductDiscount(order);
	const subTotal = getOriginalSubtotal(order);
	const percDisc = (totalDisc / subTotal) * 100;

	return `(${parseFloat(percDisc).toFixed(0)}%)`;
}

function createLineItemData(product, order, products) {
	/**
	 * Finds the product in the order and returns its quantity.
	 * The quantity is not available in the product object, so it has to be found in the Orders object.
	 * If the product is unable to be found in the Orders object, it defaults to 1. (It shouldn't be 0 because it's in the order.)
	 */
	const bcOrderProduct = order.products.find(
		({ id }) => id === product.bc_order_item_id,
	);
	const quantity = bcOrderProduct?.quantity ?? 1;
	const parentId = getOptionsKeyValue(bcOrderProduct, 'parentId');
	const addOnsParentId = getOptionsKeyValue(bcOrderProduct, 'addOnsParentId');

	// copy all the questions that are on the product and add the variant option selections to be displayed correctly
	const productQuestionsWithVariations = {
		...product.questions,
		...createVariationQuestions(bcOrderProduct),
	};
	const vehicle =
		getProductVehicle(product) ||
		getStandaloneAddonVehicle(product, products, order.products);

	return {
		...product,
		type: product.name,
		vehicle,
		description: product.seat,
		quantity,
		child_products: [],
		addOnsParentId: addOnsParentId || null,
		parentId: parentId || null,
		price: parseFloat(bcOrderProduct?.total_inc_tax ?? 0).toFixed(2),
		originalProduct: product,
		questions: productQuestionsWithVariations,
	};
}

const StyledTableGroupHeaderCell = styled(StyledTableHeaderCell)`
	background-color: ${({ theme }) => theme.colors.secondary60};
	color: ${({ theme }) => theme.colors.secondary10};
	font-weight: 400;
`;

const ToolTipWrapperFix = styled.div`
	& > * {
		display: block;
	}
`;

const StyledTotalsTableCell = styled(StyledTableDataCell).attrs({
	withPadding: true,
})`
	background: #eceef5;
	padding: ${({ theme }) => `0.25rem ${theme.spacing.small}`};

	tr:first-of-type & {
		padding-top: ${({ theme }) => theme.spacing.small};
	}

	tr:last-of-type & {
		padding-bottom: ${({ theme }) => theme.spacing.small};
		border-bottom: 1px solid #d9dce9;
	}
`;

const StyledTotalsLabelCell = styled(StyledTotalsTableCell)`
	text-align: left;
	font-weight: bold;
	border-left: 1px solid #d9dce9;
	width: 130px;
`;

const StyledTotalsValueCell = styled(StyledTotalsTableCell)`
	text-align: right;
	border-right: 1px solid #d9dce9;
`;

function TotalsRow({ label, value, className }) {
	return (
		<StyledTableRow className={className}>
			<StyledTableDataCell colSpan={6} />
			<StyledTotalsLabelCell>{label}</StyledTotalsLabelCell>
			<StyledTotalsValueCell colSpan={2}>{value}</StyledTotalsValueCell>
			<StyledTableDataCell />
		</StyledTableRow>
	);
}

const StyledGrandTotalsRow = styled(TotalsRow)`
	${StyledTotalsValueCell} {
		font-weight: bold;
	}
`;

function LineItemsTable({ data, order, orderDispatch }) {
	const [lineItem, setLineItem] = useState(null);
	const [vendors, setVendors] = useState([]);
	const client = useClient();

	useEffect(() => {
		client
			.getVendors()
			.then((vendorsList) => {
				setVendors(
					createSelectOptions(
						vendorsList.filter((vendor) => vendor !== ''),
					),
				);
				return false;
			})
			.catch((e) => console.error(e));
	}, [client]);

	function handleVendorChange(selectedVendor, products) {
		orderDispatch({
			type: ORDER_ACTIONS.UPDATE_VENDOR,
			data: { products, vendor: selectedVendor },
		});
	}

	function handleAddressKey(event, products) {
		if (event.key !== 'Enter') return;

		orderDispatch({
			type: ORDER_ACTIONS.UPDATE_SHIPPING_ADDRESS,
			data: { products, shippingAddress: event.currentTarget.value },
		});
	}

	function updateLineItem(updatedLineItem) {
		orderDispatch({
			type: ORDER_ACTIONS.UPDATE_LINE_ITEM,
			data: updatedLineItem,
		});
		setLineItem(null);
	}

	function renderAnswer(answer, question, row) {
		if (row.vendor !== 'DashDesigns' || question !== 'Configuration')
			return answer;

		try {
			const answers = JSON.parse(answer);
			return answers.map((answerData) => (
				<div key={answerData.option}>
					<span>{answerData.option}: </span>
					<span>{answerData.value}</span>
				</div>
			));
		} catch (error) {
			return answer;
		}
	}

	function getLineItemColorOptions(lineItem) {
		const colorOptions = order.colorOptions;
		const bcItemId = lineItem.bc_item_id;
		let colorValues = [];

		colorOptions.forEach((options) => {
			if (options.product_id === bcItemId) colorValues = options.colors;
		});

		return colorValues;
	}

	return (
		<>
			<StyledTable withBorder={true} style={{ background: '#FFF' }}>
				<StyledTableHead>
					<StyledTableRow>
						<StyledTableHeaderCell>Type</StyledTableHeaderCell>
						<StyledTableHeaderCell>Color</StyledTableHeaderCell>
						<StyledTableHeaderCell>Vehicle</StyledTableHeaderCell>
						<StyledTableHeaderCell>Row</StyledTableHeaderCell>
						<StyledTableHeaderCell>Diagram</StyledTableHeaderCell>
						<StyledTableHeaderCell>
							Description
						</StyledTableHeaderCell>
						<StyledTableHeaderCell>
							Part Number
						</StyledTableHeaderCell>
						<StyledTableHeaderCell style={{ textAlign: 'center' }}>
							Quantity
						</StyledTableHeaderCell>
						<StyledTableHeaderCell style={{ textAlign: 'right' }}>
							Price
						</StyledTableHeaderCell>
						<StyledTableHeaderCell
							style={{ textAlign: 'center' }}
						/>
					</StyledTableRow>
				</StyledTableHead>
				<StyledTableBody>
					{data.map((vendorRow) => (
						<React.Fragment key={vendorRow.name}>
							<StyledTableRow>
								<StyledTableGroupHeaderCell>
									Vendor
								</StyledTableGroupHeaderCell>
								<StyledTableGroupHeaderCell colSpan={2}>
									<Select
										required
										style={{ width: '100%' }}
										options={vendors}
										value={vendorRow.name}
										onOptionChange={(selectedValue) =>
											handleVendorChange(
												selectedValue,
												vendorRow.products.map(
													({ id }) => id,
												),
											)
										}
									/>
								</StyledTableGroupHeaderCell>
								<StyledTableGroupHeaderCell>
									Ship to:
								</StyledTableGroupHeaderCell>
								<StyledTableGroupHeaderCell colSpan={6}>
									<ToolTipWrapperFix>
										<Tooltip
											style={{ width: '100%' }}
											trigger={
												<Input
													required
													onKeyDown={(event) =>
														handleAddressKey(
															event,
															vendorRow.products.map(
																({ id }) => id,
															),
														)
													}
													defaultValue={
														vendorRow.shipping_address
													}
												/>
											}
											placement="top"
										>
											Press 'Enter' to change
										</Tooltip>
									</ToolTipWrapperFix>
								</StyledTableGroupHeaderCell>
							</StyledTableRow>
							{vendorRow.products.map((row) => (
								<React.Fragment key={row.bc_product_id}>
									<StyledTableRow>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
										>
											{row.type}
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
										>
											{row.color}
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
										>
											{row.vehicle}
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
										>
											{row.row}
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
										>
											<span>
												{row.diagram && (
													<img
														className="product-diagram"
														src={`https://cdn11.bigcommerce.com/s-le3vzvrwix/product_images/uploaded_images/${row.diagram.toLowerCase()}.jpg`}
														alt="Diagram"
													/>
												)}
											</span>
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
										>
											<p
												style={{
													marginTop: '0',
													marginBottom:
														Object.keys(
															row.questions || {},
														).length > 0
															? '1rem'
															: '0',
												}}
											>
												{row.description}
											</p>
											{Object.keys(row.questions || {})
												.length > 0 && (
												<>
													<H4 margin="none">
														Questions:
													</H4>
													{Object.entries(
														row.questions,
													).map(
														([question, answer]) =>
															question !==
																'Trim Comment' && (
																<p
																	key={
																		question
																	}
																	style={{
																		margin: '0',
																	}}
																>
																	{row.vendor ===
																		'DashDesigns' &&
																	question ===
																		'Configuration'
																		? ''
																		: `${question}: `}
																	{renderAnswer(
																		answer,
																		question,
																		row,
																	)}
																</p>
															),
													)}
												</>
											)}
											{row.notes && (
												<>
													<H4 margin="none">
														Comments:
													</H4>
													<p
														style={{
															margin: '0',
														}}
													>
														{row.notes}
													</p>
												</>
											)}
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
										>
											{row.part_number}
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
											style={{ textAlign: 'center' }}
										>
											<span
												style={
													row.quantity > 1
														? highQuantityStyle
														: undefined
												}
											>
												{row.quantity}
											</span>
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
											style={{ textAlign: 'right' }}
										>
											{row.price}
										</StyledTableDataCell>
										<StyledTableDataCell
											withPadding={true}
											withBorder={true}
											style={{ textAlign: 'center' }}
										>
											<Link
												variant="primary"
												onClick={() =>
													setLineItem(
														row.originalProduct,
													)
												}
											>
												Edit
											</Link>
										</StyledTableDataCell>
									</StyledTableRow>
									{row.child_products.map(
										(
											addonProduct,
											index,
											childProducts,
										) => (
											<StyledTableRow
												key={addonProduct.bc_product_id}
											>
												<StyledTableDataCell
													colSpan={5}
													style={{
														background: '#f6f7f9',
														textAlign: 'right',
													}}
													withPadding={true}
													withBorder={
														index ===
														childProducts.length - 1
													}
												>
													<H3 margin="none">
														{index === 0 ? (
															'Optional Upgrades'
														) : (
															<>&nbsp;</>
														)}
													</H3>
												</StyledTableDataCell>
												<StyledTableDataCell
													colSpan={2}
													style={{
														background: '#f6f7f9',
													}}
													withPadding={true}
													withBorder={true}
												>
													{addonProduct.type}
												</StyledTableDataCell>
												<StyledTableDataCell
													style={{
														background: '#f6f7f9',
														textAlign: 'center',
													}}
													withPadding={true}
													withBorder={true}
												>
													{addonProduct.quantity}
												</StyledTableDataCell>
												<StyledTableDataCell
													style={{
														background: '#f6f7f9',
														textAlign: 'right',
													}}
													withPadding={true}
													withBorder={true}
												>
													{addonProduct.price}
												</StyledTableDataCell>
												<StyledTableDataCell
													style={{
														background: '#f6f7f9',
														textAlign: 'center',
													}}
													withPadding={true}
													withBorder={true}
												>
													<Link
														variant="primary"
														onClick={() =>
															setLineItem(
																addonProduct.originalProduct,
															)
														}
													>
														Edit
													</Link>
												</StyledTableDataCell>
											</StyledTableRow>
										),
									)}
								</React.Fragment>
							))}
						</React.Fragment>
					))}
				</StyledTableBody>
				<tfoot>
					<TotalsRow
						label="Subtotal"
						value={`$${getOriginalSubtotal(order)}`}
					/>
					<TotalsRow
						label={`Discount ${getDiscountPerc(order)}`}
						value={`$${getProductDiscount(order)}`}
					/>
					<TotalsRow
						label="Promo Code"
						value={`$${(
							parseFloat(order.subtotal_ex_tax).toFixed(2) -
							parseFloat(order.total_ex_tax).toFixed(2)
						).toFixed(2)}`}
					/>
					<TotalsRow
						label="Sale Subtotal"
						value={`$${parseFloat(order.total_ex_tax).toFixed(2)}`}
					/>
					<TotalsRow
						label="Shipping"
						value={`$${parseFloat(
							order.shipping_cost_ex_tax,
						).toFixed(2)}`}
					/>
					<TotalsRow
						label="Tax"
						value={`$${parseFloat(order.total_tax).toFixed(2)}`}
					/>
					<StyledGrandTotalsRow
						label="Grand Total"
						value={`$${parseFloat(order.total_inc_tax).toFixed(
							2,
						)} ${order.currency_code}`}
					/>
				</tfoot>
			</StyledTable>

			{lineItem !== null && (
				<LineItemEditForm
					lineItem={lineItem}
					onLineItemChange={setLineItem}
					onCancel={() => setLineItem(null)}
					onComplete={updateLineItem}
					colorOptions={getLineItemColorOptions(lineItem)}
				/>
			)}
		</>
	);
}

function getOption(options, displayName) {
	return options.find(
		(option) =>
			option.display_name.toLowerCase() === displayName.toLowerCase(),
	);
}

export default function LineItems({ custom, order, orderDispatch }) {
	// const [isExporting, exportVendorOrder] = usePdfExport(order.id);
	const TRIM_COMMENT_NAME = 'Trim Comment';

	const trimComments = new Set(
		order.products
			.map(
				(item) =>
					getOption(item.product_options, TRIM_COMMENT_NAME)?.value,
			)
			.filter(Boolean),
	);

	const trimCommentsString = [...trimComments].join(' ');

	const lineItemsByVendor = useMemo(() => {
		const vendors = new Map();
		const addonsByParentId = new Map();

		for (const product of custom.products) {
			const vendor = vendors.get(product.vendor) || {
				name: product.vendor,
				shipping_address: product.shipping_address,
				products: [],
			};

			const lineItem = createLineItemData(
				product,
				order,
				custom.products,
			);

			// This line item is an addon, we need to nest it in its parent
			if (shouldNestAddonLineItem(lineItem)) {
				const parentProduct = vendor.products.find(
					({ addOnsParentId }) =>
						lineItem.parentId === addOnsParentId,
				);

				const parentProductAddons =
					addonsByParentId.get(lineItem.parentId) || [];

				// Our parent hasn't been added yet, so store it for later
				if (!parentProduct) {
					parentProductAddons.push(lineItem);
					addonsByParentId.set(
						lineItem.parentId,
						parentProductAddons,
					);

					// Can't do anything else with this line item at the moment
					continue;
				}

				parentProduct.child_products.push(lineItem);
				vendors.set(product.vendor, vendor);
				continue;
			}

			// Make sure to grab any addons waiting to be added
			if (lineItem.addOnsParentId) {
				lineItem.child_products =
					addonsByParentId.get(lineItem.addOnsParentId) || [];
				// We've consumed these waiting addons; avoid duplicating later
				addonsByParentId.delete(lineItem.addOnsParentId);
			}

			vendor.products.push(lineItem);
			vendors.set(product.vendor, vendor);
		}

		// Any remaining addons that never found a parent should render as standalone
		for (const orphanAddons of addonsByParentId.values()) {
			for (const orphan of orphanAddons) {
				const vendor = vendors.get(orphan.vendor) || {
					name: orphan.vendor,
					shipping_address: orphan.shipping_address,
					products: [],
				};
				vendor.products.push(orphan);
				vendors.set(orphan.vendor, vendor);
			}
		}

		return [...vendors.values()];
	}, [custom, order]);

	return (
		<Panel header="Products" marginTop="Large">
			<Box>
				{(order.staff_notes || trimCommentsString) && (
					<OrderComment header="Order Comments">
						{order.staff_notes && <p>{order.staff_notes}</p>}
						{trimCommentsString && <p>{trimCommentsString}</p>}
					</OrderComment>
				)}
				<br />
				<LineItemsTable
					order={order}
					data={lineItemsByVendor}
					orderDispatch={orderDispatch}
				/>
			</Box>
		</Panel>
	);
}
